import { createRouter, createWebHistory } from "vue-router";
import { AuthApi } from "sandpiper_api";
import log from "@/utils/logger";

import store from "@/store";

const routes = [
    {
        path: "/login",
        name: "login",
        // route level code-splitting
        // this generates a separate chunk (about.[hash].js) for this route
        // which is lazy-loaded when the route is visited.
        component: () => import(/* webpackChunkName: "login" */ "@/views/Login.vue"),
        meta: { navMode: "login" }
    },
    {
        path: "/register",
        name: "register",
        component: () => import(/* webpackChunkName: "login" */ "@/views/Register"),
        meta: { navMode: "login" }
    },
    {
        path: "/register-confirmed",
        name: "register-confirmed",
        component: () => import(/* webpackChunkName: "login" */ "@/views/RegisterConfirmed"),
        meta: { navMode: "login" }
    },
    {
        path: "/forgot-password",
        name: "forgot-password",
        component: () => import(/* webpackChunkName: "login" */ "@/views/ForgotPassword.vue"),
        meta: { navMode: "login" }
    },
    {
        path: "/reset-password",
        name: "reset-password",
        component: () => import(/* webpackChunkName: "login" */ "@/views/ResetPassword.vue"),
        meta: { navMode: "login" }
    },
    {
        path: "/activate-account",
        name: "activate-account",
        component: () => import("@/views/ActivateAccount.vue"),
        meta: { navMode: "login" }
    },
    {
        path: "/logout",
        name: "logout",
        component: {
            beforeRouteEnter(to, from, next)
            {
                if (store.getters["security/isAuthenticated"])
                {
                    doLogout()
                        .finally(() =>
                        {
                            next({ name: "login" });
                        });
                }
                else
                {
                    next({ name: "login" });
                }
            }
        }
    },
    {
        path: "/legal",
        name: "legal",
        // route level code-splitting
        // this generates a separate chunk (about.[hash].js) for this route
        // which is lazy-loaded when the route is visited.
        component: () => import(/* webpackChunkName: "legal" */ "@/views/Legal.vue"),
    },
    {
        path: "/agenda",
        alias: "/",
        component: () => import(/* webpackChunkName: "agenda" */ "@/views/Agenda.vue"),
        meta: { requiresAuth: true },
        children: [
            {
                path: "",
                name: "agenda",
                alias: "/",
                component: () => import(/* webpackChunkName: "agenda" */ "@/views/Agenda/AgendaMain.vue"),
                meta: { requiresAuth: true }
            },
            {
                path: "plan-activity",
                name: "plan-activity",
                component: () => import(/* webpackChunkName: "agenda" */ "@/views/Agenda/PlanActivity.vue"),
                meta: { requiresAuth: true }
            },
            {
                path: "plan-activity/:id",
                name: "edit-planned-activity",
                component: () => import(/* webpackChunkName: "agenda" */ "@/views/Agenda/PlanActivity.vue"),
                meta: { requiresAuth: true }
            },
            {
                path: "activities/:id",
                name: "activity-detailed",
                component: () => import(/* webpackChunkName: "agenda" */ "@/views/Agenda/ActivityDetailed.vue"),
                meta: { requiresAuth: true }
            },
            {
                path: "end-game-confirmation",
                name: "end-game-confirmation",
                component: () => import(/* webpackChunkName: "agenda" */ "@/views/Agenda/EndGameConfirmation.vue"),
                meta: { requiresAuth: true }
            },
        ]
    },
    {
        path: "/events/add",
        name: "event-add",
        component: () => import("@/views/Events/EventEdit.vue")
    },
    {
        path: "/events/:id/edit",
        name: "event-edit",
        component: () => import("@/views/Events/EventEdit.vue")
    },
    {
        path: "/activities",
        component: () => import(/* webpackChunkName: "quests" */ "@/views/Quests.vue"),
        meta: { requiresAuth: true },
        children: [
            {
                path: "",
                name: "quests",
                component: () => import(/* webpackChunkName: "quests" */ "@/views/Quests/QuestsMain.vue"),
                meta: { requiresAuth: true }
            },
            {
                path: "activity/:id",
                name: "quest-detailed",
                component: () => import(/* webpackChunkName: "quests" */ "@/views/Quests/QuestDetailed.vue"),
                meta: { requiresAuth: true }
            },
            {
                path: "grouped/:questType",
                name: "quest-detailed-grouped",
                component: () => import(/* webpackChunkName: "quests" */ "@/views/Quests/QuestDetailedGrouped.vue"),
                meta: { requiresAuth: true }
            }
        ]
    },
    {
        path: "/news",
        component: () => import(/* webpackChunkName: "news" */ "@/views/News.vue"),
        meta: { requiresAuth: true },
        children: [
            {
                path: "",
                name: "news-overview",
                component: () => import(/* webpackChunkName: "news" */ "@/views/News/NewsMain.vue"),
                meta: { requiresAuth: true }
            },
            {
                path: "article/:id",
                name: "article-detailed",
                component: () => import(/* webpackChunkName: "news" */ "@/views/News/ArticleDetailed.vue"),
                meta: { requiresAuth: true }
            }
        ]
    },
    {
        path: "/profile",
        component: () => import(/* webpackChunkName: "profile" */ "@/views/Profile.vue"),
        meta: { requiresAuth: true },
        children: [
            {
                path: "",
                name: "profile",
                component: () => import(/* webpackChunkName: "profile" */ "@/views/Profile/ProfileMain.vue"),
                meta: { requiresAuth: true }
            },
            {
                path: "edit",
                name: "edit-profile",
                component: () => import(/* webpackChunkName: "profile" */ "@/views/Profile/EditProfile.vue"),
                meta: { requiresAuth: true },
                props: true
            },
            {
                path: "delete",
                name: "delete-profile",
                component: () => import(/* webpackChunkName: "profile" */ "@/views/Profile/DeleteProfile.vue"),
                meta: { requiresAuth: true }
            },
            {
                path: "change-password",
                name: "change-password",
                component: () => import(/* webpackChunkName: "profile" */ "@/views/Profile/ChangePassword.vue"),
                meta: { requiresAuth: true }
            },
            {
                path: "create-buddy",
                name: "create-buddy",
                component: () => import(/* webpackChunkName: "profile" */ "@/views/Profile/CreateBuddy.vue"),
                meta: { requiresAuth: true }
            },
            {
                path: "edit-buddy/:id",
                name: "edit-buddy",
                component: () => import(/* webpackChunkName: "profile" */ "@/views/Profile/CreateBuddy.vue"),
                meta: { requiresAuth: true },
                props: (route) =>
                {
                    return {
                        ...route.params,
                        initialStudentCount: parseInt(route.params.initialStudentCount),
                        initialSchoolId: parseInt(route.params.initialSchoolId)
                    }
                }
            },
            {
                path: "buddy-detail/:id",
                name: "buddy-detail",
                component: () => import(/* webpackChunkName: "profile" */ "@/views/Profile/BuddyDetail.vue"),
                meta: { requiresAuth: true }
            },
            {
                path: "buddy-diploma",
                name: "buddy-diploma",
                component: () => import(/* webpackChunkName: "profile" */ "@/views/Profile/Diploma.vue"),
                meta: { requiresAuth: true, hideFooter: true }
            }
        ]
    },
    {
        path: "/puzzle-test",
        name: "puzzle-test",
        component: () => import("@/views/PuzzleOrgTest.vue")
    },
    {
        path: "/student-platform/:id",
        name: "student-platform",
        component: () => import("@/views/StudentViews/StudentPlatform.vue"),
        meta: { hideHeader: true, hideFooter: true }
    },
    {
        path: "/dev-buddy-color-picker",
        name: "dev-buddy-color-picker",
        component: () => import("@/views/StudentViews/DevBuddyColorPicker.vue"),
        meta: { hideHeader: true, hideFooter: true }
    },
    {
        path: "/:catchAll(.*)",
        component: () => import("@/views/NotFound.vue"),
    },
]

function doLogout()
{
    // let auth = ApiClient.instance.authentications["oAuth2Password"];
    // auth.accessToken = null;
    // /auth/logout can't access the refresh token in the cookie and therefore can't invalidate it, workaround to
    // invalidate it via the same url as the refresh endpoint with an optional flag.
    return new AuthApi().refreshTokens({ invalidateToken: true }).finally(() =>
    {
        return store.dispatch("security/logout");
    });
}

const router = createRouter({
    history: createWebHistory(),
    routes
})

router.beforeEach((to, from, next) =>
{
    log.debug("ROUTE - %o\nRequired auth: %s\nCurrent token: %s", to, to.meta.requiresAuth, store.state.security.accessToken);

    if (to.meta.requiresAuth)
    {
        if (store.getters["security/isAuthenticated"])
        {
            next();
        }
        else
        {
            log.debug("Trying to access secure page, but no access token was found.");

            store.dispatch("security/refreshAccessToken")
                .then(() => next())
                .catch((e) =>
                {
                    // If any error occurred while trying to refresh the access token, redirect to the login page
                    log.info("Auth error, unable to refresh access token. Redirecting to login page. - %o", e);
                    next({ name: "login" });
                });
        }
    }
    else
    {
        next();
    }
});

export default router;
